import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IonIcon } from "@ionic/react";
import { closeOutline, timeOutline, checkmarkCircle, documentTextOutline } from "ionicons/icons";
import MintSlipLogo from "@/assests/mintslip-logo.png";
import { docBasePrice } from "@/utils/docPrices";
import { Stars, money } from "./Paywall";
import "@/styles/paywall.css";

// Where the success page leaves the offer for the app, and where the chosen
// discount lives while the buyer builds the matching document (read by
// CheckoutWithPaywall, enforced by the backend's per-IP window).
export const UPSELL_OFFER_KEY = "ms:upsellOffer";
export const UPSELL_TAKEN_KEY = "ms:upsell";

export function readPendingUpsell() {
  try {
    const raw = sessionStorage.getItem(UPSELL_OFFER_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || !o.expiresAt || new Date(o.expiresAt) <= new Date()) { sessionStorage.removeItem(UPSELL_OFFER_KEY); return null; }
    if (!Array.isArray(o.targets) || !o.targets.length || !(o.discountPercent > 0)) return null;
    return o;
  } catch { return null; }
}
export function clearPendingUpsell() { try { sessionStorage.removeItem(UPSELL_OFFER_KEY); } catch {} }

// Post-purchase upsell in the paywall's clothes: the same full-screen chrome,
// badge, title, stars and countdown, and the matching documents on offer as
// the green plan rows, one per document. Tapping a row records the discount
// for that document's checkout and opens its generator.
export default function UpsellPaywall({ offer, boughtLabel, previewImage = null, onPick, onDismiss }) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [chosen, setChosen] = useState(null); // the row the buyer tapped (first row until then)
  const pct = offer.discountPercent;
  const targets = offer.targets;

  useLayoutEffect(() => {
    const deadline = new Date(offer.expiresAt).getTime();
    const tick = () => setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [offer.expiresAt]);

  // The window closed while the screen was up: nothing left to offer
  useEffect(() => {
    if (secondsLeft === 0 && new Date(offer.expiresAt).getTime() <= Date.now()) onDismiss?.();
  }, [secondsLeft]); // eslint-disable-line

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const priceOf = (t) => Number(t.price ?? docBasePrice(t.documentType) ?? 0);
  const discounted = (base) => Math.max(0.5, Math.round(base * (100 - pct)) / 100);

  return createPortal(
    <div className="pw pw-upsell">
      {previewImage
        ? <img className="pw-bgimg" src={previewImage} alt="" aria-hidden="true" />
        : <span className="pw-bgfill" aria-hidden="true" />}
      <span className="pw-scrim" aria-hidden="true" />

      <button className="pw-x" onClick={onDismiss} aria-label="Close">
        <IonIcon icon={closeOutline} />
      </button>

      <div className="pw-brandbar">
        <img className="pw-logo" src={MintSlipLogo} alt="MintSlip" />
      </div>

      <div className="pw-head pw-swap" key="upsell-head">
        <span className="pw-badge"><IonIcon icon={timeOutline} /> {pct}% Off, Limited Time</span>
        <h1 className="pw-title">
          {targets.length === 1 ? `Need the matching ${targets[0].label}?` : "Need a matching document?"}
        </h1>
        <p className="pw-subtitle">
          {boughtLabel ? `Customers who buy ${/^[aeiou]/i.test(boughtLabel) ? "an" : "a"} ${boughtLabel} usually need these too` : "The #1 Document Creation App"}
        </p>
        <Stars />
        <p className="pw-expires">Offer expires in</p>
        <div className="pw-countdown">
          <div className="pw-cd-box">
            <span className="pw-cd-num">{mm}</span>
            <span className="pw-cd-label">Minutes</span>
          </div>
          <span className="pw-cd-sep">:</span>
          <div className="pw-cd-box">
            <span className="pw-cd-num">{ss}</span>
            <span className="pw-cd-label">Seconds</span>
          </div>
        </div>
      </div>

      <div className="pw-foot pw-swap" key="upsell-foot">
        <div className="pw-feature"><IonIcon icon={checkmarkCircle} /> Instant download · No subscription</div>
        <div className="pw-plans">
          {targets.map((t) => {
            const base = priceOf(t);
            const now = base ? discounted(base) : 0;
            const selected = (chosen?.documentType || targets[0]?.documentType) === t.documentType;
            return (
              <button type="button" className={`pw-plan pw-plan-btn${selected ? " is-selected" : ""}`} key={t.documentType} aria-pressed={selected} onClick={() => setChosen(t)}>
                <span className="pw-plan-info">
                  <strong>{t.label}</strong>
                  <span>One-time purchase</span>
                </span>
                <span className="pw-plan-price">
                  {base ? (
                    <>
                      <strong><s className="pw-strike">{money(base)}</s>{money(now)}</strong>
                      <span className="pw-plan-save">You save {money(base - now)}</span>
                    </>
                  ) : (
                    <strong>{pct}% off</strong>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        {/* Rows only pick; this button (the paywall's "Complete for" style) opens the chosen generator */}
        <button className="pw-cta" onClick={() => onPick?.(chosen || targets[0])}>
          <IonIcon icon={documentTextOutline} /> Generate document
        </button>
        <p className="pw-fine">One-time purchase. No subscription required.</p>
      </div>
    </div>,
    // Into ion-app (its own stacking context) so the screen sits above the
    // notifications drawer that opens with the download on the way back
    document.querySelector("ion-app") || document.body,
  );
}
